import { Component, OnDestroy } from '@angular/core';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { Observable, forkJoin, Subscription } from 'rxjs';
import * as _ from 'lodash';
import { environment } from '@environment';
import { AppService } from '@services/app.service';
import { ConfigValueCacheService } from '@services/config-value-cache.service';
import { AppLang } from '@enums/enum';
import { NavService } from '@services/nav.sevice';
import { HttpErrorResponse } from '@angular/common/http';
import { Router, NavigationEnd, ActivatedRoute, PRIMARY_OUTLET, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs/operators';
import { BreadcrumbInterface } from '@interfaces/breadcrumb.interface';
import { slideFadeinUp, slideFadeinRight, zoomFadein, fadein } from '@shared/animations/router.animation';
import * as version from '../../version';

@Component({
    template: `
        <ng-container *ngIf="data$ | async; else loader">
            <app-navbar class="d-print-none"></app-navbar>
            <div class="container-fluid admin-container">
                <div class="row no-gutters">
                    <div class="col-12 outlet-container">
                        <nav class="d-print-none" aria-label="breadcrumb" *ngIf="breadcrumbs.length">
                            <ol class="breadcrumb breadcrumb-style1 mg-b-0">
                                <li class="breadcrumb-item" *ngFor="let breadcrumb of breadcrumbs; let index = index">
                                    <a [ngClass]="{ active: index === breadcrumbs.length - 1 }" class="tx-normal tx-9" [routerLink]="[breadcrumb.url]">
                                        {{ breadcrumb.label | translate }}

                                        <span class="tx-semibold" *ngIf="breadcrumb.params['key']">[{{ breadcrumb.params['key'] }}]</span>
                                    </a>
                                </li>
                                <!--<li (click)="navigateBack()" class="breadcrumb-item tx-semibold c-pointer"><i-feather name="arrow-left" [style.width.px]="10" [style.height.px]="10"></i-feather> {{'Back' | translate}}</li>-->
                            </ol>
                        </nav>
                        <div [@slideFadeinRight]="currentPath">
                            <router-outlet #outlet="outlet"></router-outlet>
                        </div>
                    </div>
                </div>
            </div>

            <div class="col-12 footer outlet-container d-print-none d-flex justify-content-between align-items-center" [ngClass]="{ 'no-production': !isProd }">
                <div>
                    Proudly Powered by&nbsp;
                    <a title="Open Routing" href="http://open-routing.com">Open Routing</a>
                    &nbsp;|&nbsp;Copyright &copy; 2018-{{ year }}&nbsp;
                    <span *ngIf="!isProd">Version: {{ version }}</span>
                </div>
            </div>
        </ng-container>

        <ng-template #loader>
            <div class="content content-fixed content-auth-alt" [style.height.vh]="100">
                <div *ngIf="loader" class="load-spinner text-center">
                    <i class="fa fa-spinner fast-pulse" aria-hidden="true"></i>
                </div>
            </div>
        </ng-template>
    `,
    styleUrls: ['./main.component.scss'],
    animations: [slideFadeinUp, slideFadeinRight, zoomFadein, fadein],
})
export class MainComponent implements OnDestroy {
    public loader: boolean = true;
    public error: HttpErrorResponse;
    public breadcrumbs: BreadcrumbInterface[] = [];
    public data$: Observable<any>;
    public currentPath: string | null = null;
    private readonly configValuesSubscription: Subscription;

    public year: number = new Date().getFullYear();

    get isProd() {
        return environment.production;
    }

    get version() {
        return version.v.version;
    }

    constructor(
        private readonly appService: AppService,
        private readonly configValueCacheService: ConfigValueCacheService,
        private readonly navService: NavService,
        private readonly router: Router,
        private readonly activatedRoute: ActivatedRoute,
        private readonly translateService: TranslateService
    ) {
        const ROUTE_DATA_BREADCRUMB: string = 'breadcrumb';

        this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event) => {
            const root: ActivatedRoute = this.activatedRoute.root;
            this.breadcrumbs = this.getBreadcrumbs(root);

            this.breadcrumbs.unshift({
                label: 'Home',
                params: {},
                url: '/routes',
            });

            this.breadcrumbs = _.uniqBy(this.breadcrumbs, (b: BreadcrumbInterface) => b.label);
        });

        const depots$ = this.appService.getWarehouses();
        const userAppSettings$ = this.appService.getUserAppSettings();

        this.data$ = forkJoin(depots$, userAppSettings$);

        this.configValuesSubscription = this.configValueCacheService.getAll().subscribe();

        this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
            moment.locale(event.lang);
            localStorage.setItem('lang', event.lang);
            this.loader = false;
        });

        const lang = localStorage.getItem('lang') || AppLang.PL;
        this.translateService.setDefaultLang(lang);
        this.translateService.use(lang);
    }

    public ngOnDestroy(): void {
        this.configValuesSubscription.unsubscribe();
    }

    public back() {
        localStorage.removeItem('user');
        localStorage.removeItem('warehouse');
        this.navService.goToPage('login');
    }

    public navigateBack() {
        return this.navService.navigateBack();
    }

    private getBreadcrumbs(route: ActivatedRoute, url: string = '', breadcrumbs: BreadcrumbInterface[] = []): BreadcrumbInterface[] {
        const ROUTE_DATA_BREADCRUMB: string = 'breadcrumb';

        const children: ActivatedRoute[] = route.children;

        if (children.length === 0) {
            return breadcrumbs;
        }

        for (const child of children) {
            if (child.outlet !== PRIMARY_OUTLET) {
                continue;
            }

            if (!child.snapshot.data.hasOwnProperty(ROUTE_DATA_BREADCRUMB)) {
                return this.getBreadcrumbs(child, url, breadcrumbs);
            }

            const routeURL: string = child.snapshot.url.map((segment) => segment.path).join('/');
            url += `/${encodeURI(routeURL)}`;

            let key = Object.keys(child.snapshot.params)[0];
            key = key ? key.replace('Id', '') : undefined;

            const paramObj = key && child.snapshot.data[key] ? child.snapshot.data[key].name : '';
            const breadcrumb: BreadcrumbInterface = {
                label: child.snapshot.data[ROUTE_DATA_BREADCRUMB],
                params: { key: paramObj, ...child.snapshot.params },
                url: url,
            };
            breadcrumbs.push(breadcrumb);

            return this.getBreadcrumbs(child, url, breadcrumbs);
        }
    }

    private buildQueryParams(params: Object): string {
        return Object.keys(params)
            .map((key) => key + '=' + params[key])
            .join('&');
    }

    public get isWarehouseSet(): boolean {
        if (localStorage.getItem('depot')) {
            return true;
        }
    }

    public onActivate(outlet: RouterOutlet): void {
        this.currentPath = outlet.activatedRoute?.routeConfig?.path || null;
    }
}
